import type { components } from '@/types/api'
import { api } from './client'

/**
 * Typed calls to the API.
 *
 * Response shapes come from `@/types/api`, which is generated from the OpenAPI
 * document the server emits at build time. Hand-written interfaces here would
 * drift from the server the first time a field is renamed; generated ones turn
 * that into a compile error (SPEC §13.4).
 */

export type PingResponse = components['schemas']['PingResponse']
export type RegisterOrganizerRequest = components['schemas']['RegisterOrganizerRequest']
export type RegistrationAcceptedResponse = components['schemas']['RegistrationAcceptedResponse']
export type SignInOrganizerRequest = components['schemas']['SignInOrganizerRequest']
export type OrganizerSessionResponse = components['schemas']['OrganizerSessionResponse']
export type ForgotPasswordRequest = components['schemas']['ForgotPasswordRequest']
export type ResetPasswordRequest = components['schemas']['ResetPasswordRequest']
export type CreateGroupRequest = components['schemas']['CreateGroupRequest']
export type GroupResponse = components['schemas']['GroupResponse']
export type AttentionResponse = components['schemas']['AttentionResponse']
export type AttentionItemResponse = components['schemas']['AttentionItemResponse']
export type FamilyHistoryResponse = components['schemas']['FamilyHistoryResponse']
export type AddFamilyEmailRequest = components['schemas']['AddFamilyEmailRequest']
export type AddFamilyEmailResponse = components['schemas']['AddFamilyEmailResponse']
export type FamilyMealHistoryResponse = components['schemas']['FamilyMealHistoryResponse']
export type UpdateGroupRequest = components['schemas']['UpdateGroupRequest']
export type GroupOrganizerResponse = components['schemas']['GroupOrganizerResponse']
export type AuditPageResponse = components['schemas']['AuditPageResponse']
export type AuditEntryResponse = components['schemas']['AuditEntryResponse']
export type AuditActionResponse = components['schemas']['AuditActionResponse']
export type ReminderRuleResponse = components['schemas']['ReminderRuleResponse']
export type UnsubscribeResponse = components['schemas']['UnsubscribeResponse']
export type ChangeNoticeRequest = components['schemas']['ChangeNoticeRequest']
export type ChangeNoticePreviewResponse = components['schemas']['ChangeNoticePreviewResponse']
export type QueuedReminderResponse = components['schemas']['QueuedReminderResponse']
export type UpdateReminderRuleRequest = components['schemas']['UpdateReminderRuleRequest']
export type EventReminderRuleResponse = components['schemas']['EventReminderRuleResponse']
export type AddGroupOrganizerRequest = components['schemas']['AddGroupOrganizerRequest']
export type TransferOwnershipRequest = components['schemas']['TransferOwnershipRequest']
export type InvitationResponse = components['schemas']['InvitationResponse']
export type CreateInvitationRequest = components['schemas']['CreateInvitationRequest']
export type PendingInvitationResponse = components['schemas']['PendingInvitationResponse']
export type AcceptWithNewAccountRequest = components['schemas']['AcceptWithNewAccountRequest']
export type HouseholdResponse = components['schemas']['HouseholdResponse']
export type ViewAsHouseholdResponse = components['schemas']['ViewAsHouseholdResponse']
export type CreateHouseholdRequest = components['schemas']['CreateHouseholdRequest']
export type UpdateHouseholdRequest = components['schemas']['UpdateHouseholdRequest']
export type HouseholdContactResponse = components['schemas']['HouseholdContactResponse']
export type HouseholdLinkResponse = components['schemas']['HouseholdLinkResponse']
export type HouseholdSignOutResponse = components['schemas']['HouseholdSignOutResponse']
export type HouseholdInviteResponse = components['schemas']['HouseholdInviteResponse']
export type CreateHouseholdInviteRequest = components['schemas']['CreateHouseholdInviteRequest']
export type CreatedHouseholdInviteResponse = components['schemas']['CreatedHouseholdInviteResponse']
export type HouseholdInvitationOfferResponse =
  components['schemas']['HouseholdInvitationOfferResponse']
export type AcceptHouseholdInvitationRequest =
  components['schemas']['AcceptHouseholdInvitationRequest']
export type HouseholdJoinedResponse = components['schemas']['HouseholdJoinedResponse']
export type FamilyContextResponse = components['schemas']['FamilyContextResponse']
export type FamilyHouseholdResponse = components['schemas']['FamilyHouseholdResponse']
export type UpdateFamilyHouseholdRequest = components['schemas']['UpdateFamilyHouseholdRequest']
export type FamilyGroupResponse = components['schemas']['FamilyGroupResponse']
export type FamilySessionResponse = components['schemas']['FamilySessionResponse']
export type FamilyHouseholdSummaryResponse = components['schemas']['FamilyHouseholdSummaryResponse']
export type MealTemplateSummaryResponse = components['schemas']['MealTemplateSummaryResponse']
export type MealTemplateResponse = components['schemas']['MealTemplateResponse']
export type SaveMealTemplateRequest = components['schemas']['SaveMealTemplateRequest']
export type DuplicateMealTemplateRequest = components['schemas']['DuplicateMealTemplateRequest']
export type MealRotationResponse = components['schemas']['MealRotationResponse']
export type MealEventResponse = components['schemas']['MealEventResponse']
export type SaveMealEventRequest = components['schemas']['SaveMealEventRequest']
export type AssignMealTemplateRequest = components['schemas']['AssignMealTemplateRequest']
export type EventShareLinkResponse = components['schemas']['EventShareLinkResponse']
export type ReminderDraftRequest = components['schemas']['ReminderDraftRequest']
export type ReminderDraftResponse = components['schemas']['ReminderDraftResponse']
export type RecordReminderRequest = components['schemas']['RecordReminderRequest']
export type ReminderSentResponse = components['schemas']['ReminderSentResponse']
export type EventMenuResponse = components['schemas']['EventMenuResponse']
export type SaveEventMenuRequest = components['schemas']['SaveEventMenuRequest']
export type MealEventSummaryResponse = components['schemas']['MealEventSummaryResponse']
export type MealEventPageResponse = components['schemas']['MealEventPageResponse']
export type CancellationNoticeResponse = components['schemas']['CancellationNoticeResponse']
export type FamilyHomeResponse = components['schemas']['FamilyHomeResponse']
export type FamilyMealResponse = components['schemas']['FamilyMealResponse']
export type FamilyRsvpResponse = components['schemas']['FamilyRsvpResponse']
export type SaveFamilyRsvpRequest = components['schemas']['SaveFamilyRsvpRequest']
export type FamilyMenuItemResponse = components['schemas']['FamilyMenuItemResponse']
export type SaveContributionRequest = components['schemas']['SaveContributionRequest']
export type ContributionClaimResponse = components['schemas']['ContributionClaimResponse']
export type EventInvitationSetResponse = components['schemas']['EventInvitationSetResponse']
export type AttendanceSummaryResponse = components['schemas']['AttendanceSummaryResponse']

export const endpoints = {
  ping: () => api.get<PingResponse>('/api/v1/ping'),

  /**
   * Registers an organizer (SPEC §6.2).
   *
   * Always resolves the same way whether or not the address is already in use.
   * The caller must not branch on the reply to guess which happened, because
   * the server deliberately does not say (SPEC §14.9).
   */
  registerOrganizer: (body: RegisterOrganizerRequest) =>
    api.post<RegistrationAcceptedResponse>('/api/v1/organizers/register', body),

  /**
   * Signs an organizer in (SPEC §6.2).
   *
   * Every failure comes back 401 with the same message whatever caused it, so
   * there is nothing here to branch on and nothing to show beyond what the
   * server said (SPEC §14.9).
   */
  signInOrganizer: (body: SignInOrganizerRequest) =>
    api.post<OrganizerSessionResponse>('/api/v1/organizers/sign-in', body),

  signOutOrganizer: () => api.post<void>('/api/v1/organizers/sign-out'),

  /** Resolves the current session. 401 is the ordinary signed-out answer. */
  currentOrganizer: () => api.get<OrganizerSessionResponse>('/api/v1/organizers/me'),

  /**
   * Asks for a reset link (SPEC §6.2).
   *
   * Accepted whether or not the address has an account, so there is nothing in
   * the reply to branch on (SPEC §14.9).
   */
  requestPasswordReset: (body: ForgotPasswordRequest) =>
    api.post<RegistrationAcceptedResponse>('/api/v1/organizers/forgot-password', body),

  /**
   * Sets the new password.
   *
   * Carries no token: it is in an HttpOnly cookie the browser sends on its own,
   * placed when the emailed link was opened.
   */
  completePasswordReset: (body: ResetPasswordRequest) =>
    api.post<void>('/api/v1/organizers/reset-password', body),

  /** Creates a group, and the account behind it if this is the first (SPEC §6.3). */
  createGroup: (body: CreateGroupRequest) => api.post<GroupResponse>('/api/v1/groups', body),

  /**
   * The groups the signed-in organizer belongs to.
   *
   * Scoped server-side by the caller's resolved tenant, so there is nothing to
   * filter here and nothing a caller could ask for that would widen it.
   */
  listGroups: () => api.get<GroupResponse[]>('/api/v1/groups'),

  /** One group. 404 when it is not the caller's, which is also what a missing one gives. */
  getGroup: (groupId: string) => api.get<GroupResponse>(`/api/v1/groups/${groupId}`),

  groupAttention: (groupId: string) =>
    api.get<AttentionResponse>(`/api/v1/groups/${groupId}/attention`),

  /**
   * Changes a group's settings (SPEC §6.4).
   *
   * Omitted fields are left alone; an empty string clears an optional one.
   * Changing the time zone changes how times are displayed and does not move
   * anything already stored.
   */
  updateGroup: (groupId: string, body: UpdateGroupRequest) =>
    api.put<GroupResponse>(`/api/v1/groups/${groupId}`, body),

  /**
   * One page of the group's audit trail, most recent first (MT-110).
   *
   * `fromUtc` is inclusive and `toUtc` exclusive, both instants — the page
   * turns the dates somebody picked into the group's own midnights.
   */
  listGroupAudit: (
    groupId: string,
    query: {
      page?: number
      pageSize?: number
      action?: string
      fromUtc?: string
      toUtc?: string
    } = {},
  ) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') params.set(key, String(value))
    }
    const suffix = params.size > 0 ? `?${params}` : ''
    return api.get<AuditPageResponse>(`/api/v1/groups/${groupId}/audit${suffix}`)
  },

  listGroupOrganizers: (groupId: string) =>
    api.get<GroupOrganizerResponse[]>(`/api/v1/groups/${groupId}/organizers`),

  /** Owner only. 404 when the caller is not the owner, as refusal reads as absence. */
  addGroupOrganizer: (groupId: string, body: AddGroupOrganizerRequest) =>
    api.post<GroupOrganizerResponse>(`/api/v1/groups/${groupId}/organizers`, body),

  removeGroupOrganizer: (groupId: string, organizerId: string) =>
    api.delete<void>(`/api/v1/groups/${groupId}/organizers/${organizerId}`),

  transferGroupOwnership: (groupId: string, body: TransferOwnershipRequest) =>
    api.post<void>(`/api/v1/groups/${groupId}/transfer-ownership`, body),

  listInvitations: (groupId: string) =>
    api.get<InvitationResponse[]>(`/api/v1/groups/${groupId}/invitations`),

  createInvitation: (groupId: string, body: CreateInvitationRequest) =>
    api.post<InvitationResponse>(`/api/v1/groups/${groupId}/invitations`, body),

  revokeInvitation: (groupId: string, invitationId: string) =>
    api.delete<void>(`/api/v1/groups/${groupId}/invitations/${invitationId}`),

  /**
   * Describes the invitation the browser is holding, via its cookie.
   *
   * No token argument: it travels in an HttpOnly cookie placed when the
   * emailed link was opened, so it never reaches this code at all.
   */
  pendingInvitation: () => api.get<PendingInvitationResponse>('/api/v1/invitations/pending'),

  acceptInvitation: () => api.post<void>('/api/v1/invitations/accept'),

  registerFromInvitation: (body: AcceptWithNewAccountRequest) =>
    api.post<void>('/api/v1/invitations/register', body),

  listHouseholds: (groupId: string, includeArchived = false) =>
    api.get<HouseholdResponse[]>(
      `/api/v1/groups/${groupId}/households?includeArchived=${includeArchived}`,
    ),

  createHousehold: (groupId: string, body: CreateHouseholdRequest) =>
    api.post<HouseholdResponse>(`/api/v1/groups/${groupId}/households`, body),

  getHousehold: (groupId: string, householdId: string) =>
    api.get<HouseholdResponse>(`/api/v1/groups/${groupId}/households/${householdId}`),

  updateHousehold: (groupId: string, householdId: string, body: UpdateHouseholdRequest) =>
    api.put<HouseholdResponse>(`/api/v1/groups/${groupId}/households/${householdId}`, body),

  archiveHousehold: (groupId: string, householdId: string) =>
    api.post<void>(`/api/v1/groups/${groupId}/households/${householdId}/archive`),

  unarchiveHousehold: (groupId: string, householdId: string) =>
    api.post<void>(`/api/v1/groups/${groupId}/households/${householdId}/unarchive`),

  /**
   * The people in a household (SPEC §10.8).
   *
   * Masked numbers only. An organizer needs to tell two contacts apart and
   * confirm they have the right one; neither needs the whole number.
   */
  listHouseholdContacts: (groupId: string, householdId: string) =>
    api.get<HouseholdContactResponse[]>(
      `/api/v1/groups/${groupId}/households/${householdId}/contacts`,
    ),

  /** A household's current link, whole and ready to paste into a message. */
  householdLink: (groupId: string, householdId: string) =>
    api.get<HouseholdLinkResponse>(`/api/v1/groups/${groupId}/households/${householdId}/link`),

  /**
   * Issues a new link and stops the old one working (SPEC §14.2).
   *
   * Deliberately separate from signing devices out. Resetting leaves the
   * family's phones signed in, which is what makes it usable when a link has
   * gone somewhere it should not.
   */
  resetHouseholdLink: (groupId: string, householdId: string) =>
    api.post<HouseholdLinkResponse>(
      `/api/v1/groups/${groupId}/households/${householdId}/link/reset`,
    ),

  /**
   * Signs out every device in a household (SPEC §10.9).
   *
   * The link is untouched, so anybody who still has it can open it again. That
   * is the point: a lost phone does not mean sending everybody a new link.
   */
  revokeHouseholdSessions: (groupId: string, householdId: string) =>
    api.post<HouseholdSignOutResponse>(
      `/api/v1/groups/${groupId}/households/${householdId}/sessions/revoke`,
    ),

  listHouseholdInvitations: (groupId: string, householdId: string) =>
    api.get<HouseholdInviteResponse[]>(
      `/api/v1/groups/${groupId}/households/${householdId}/invitations`,
    ),

  /**
   * Creates an invitation (SPEC §6.6).
   *
   * The only response that carries the link. Only its hash is stored, so an
   * organizer who loses it creates another invitation rather than asking for
   * the same one again.
   */
  createHouseholdInvitation: (
    groupId: string,
    householdId: string,
    body: CreateHouseholdInviteRequest,
  ) =>
    api.post<CreatedHouseholdInviteResponse>(
      `/api/v1/groups/${groupId}/households/${householdId}/invitations`,
      body,
    ),

  revokeHouseholdInvitation: (groupId: string, householdId: string, inviteId: string) =>
    api.delete<void>(`/api/v1/groups/${groupId}/households/${householdId}/invitations/${inviteId}`),

  /**
   * Describes the invitation this browser is holding (SPEC §8.3).
   *
   * No token argument: it travels in an HttpOnly cookie placed when the link
   * was opened, so it never reaches this code at all (SPEC §14.4).
   */
  householdInvitation: () => api.get<HouseholdInvitationOfferResponse>('/api/v1/family/invitation'),

  /** Joins the household and starts a session (SPEC §6.7.1). */
  acceptHouseholdInvitation: (body: AcceptHouseholdInvitationRequest) =>
    api.post<HouseholdJoinedResponse>('/api/v1/family/invitation/accept', body),

  /**
   * Everything the family app needs on load (SPEC §11.4, §22.1).
   *
   * No household argument: it is inferred from the session cookie, which is
   * what stops there being one anybody could change.
   */
  familyContext: () => api.get<FamilyContextResponse>('/api/v1/family/context'),

  /**
   * Moves this session to another of the same person's households
   * (SPEC §6.8.1).
   *
   * The session moves rather than a second one opening: one browser is in one
   * household at a time.
   */
  switchFamilyHousehold: (householdId: string) =>
    api.post<void>('/api/v1/family/household', { householdId }),

  /**
   * Changes this household's own settings (SPEC §8.8).
   *
   * No household argument: it comes from the session, like everything else on
   * this surface. Returns the whole context, so the caller has one shape to
   * read rather than two that can disagree.
   */
  updateFamilyHousehold: (body: UpdateFamilyHouseholdRequest) =>
    api.put<FamilyContextResponse>('/api/v1/family/household', body),

  /**
   * Who the meal is for, and what each of them said (SPEC §6.13.1).
   *
   * The invited set, snapshotted at publication, plus the households in the
   * group that are not on it — which is what the "3 households were added
   * since this was published" prompt is built from.
   */
  eventInvitations: (groupId: string, eventId: string) =>
    api.get<EventInvitationSetResponse>(`/api/v1/groups/${groupId}/events/${eventId}/invitations`),

  /** How many are coming, counted against the invited set (SPEC §6.13). */
  eventAttendance: (groupId: string, eventId: string) =>
    api.get<AttendanceSummaryResponse>(`/api/v1/groups/${groupId}/events/${eventId}/attendance`),

  /**
   * Who still needs reminding, and the message to send them (SPEC §6.23).
   *
   * A POST despite writing nothing, because a hand-picked audience is a list
   * of households and that does not belong in a query string. Opening the
   * dialog records nothing — sending is a separate, deliberate call.
   */
  draftEventReminder: (groupId: string, eventId: string, body: ReminderDraftRequest) =>
    api.post<ReminderDraftResponse>(
      `/api/v1/groups/${groupId}/events/${eventId}/reminders/draft`,
      body,
    ),

  /**
   * Records that a reminder went out (SPEC §6.23.1).
   *
   * In MVP-Family this is what copying the message means. The method travels
   * with it so the history does not have to be reinterpreted once automatic
   * sending arrives.
   */
  recordEventReminder: (groupId: string, eventId: string, body: RecordReminderRequest) =>
    api.post<ReminderSentResponse>(`/api/v1/groups/${groupId}/events/${eventId}/reminders`, body),

  /** Stops reminder emails to the address the signed token names (MT-161). */
  unsubscribe: (token: string) =>
    api.post<UnsubscribeResponse>('/api/v1/email/unsubscribe', { token }),

  /** The group's standing reminders, in the order they go out (MT-156). */
  reminderRules: (groupId: string) =>
    api.get<ReminderRuleResponse[]>(`/api/v1/groups/${groupId}/reminder-rules`),

  updateReminderRule: (groupId: string, kind: string, body: UpdateReminderRuleRequest) =>
    api.put<ReminderRuleResponse>(`/api/v1/groups/${groupId}/reminder-rules/${kind}`, body),

  /** The group's reminders as they apply to one meal, with when each is due. */
  eventReminderRules: (groupId: string, eventId: string) =>
    api.get<EventReminderRuleResponse[]>(
      `/api/v1/groups/${groupId}/events/${eventId}/reminder-rules`,
    ),

  /** On or off for this meal only; `null` goes back to the group's setting. */
  setEventReminder: (groupId: string, eventId: string, kind: string, enabled: boolean | null) =>
    api.put<EventReminderRuleResponse>(
      `/api/v1/groups/${groupId}/events/${eventId}/reminder-rules/${kind}`,
      { enabled },
    ),

  /** Queues the reminder to be emailed to each household in the audience (MT-162). */
  queueEmailReminder: (groupId: string, eventId: string, body: ReminderDraftRequest) =>
    api.post<QueuedReminderResponse>(
      `/api/v1/groups/${groupId}/events/${eventId}/reminders/email`,
      body,
    ),

  /** Who a change or cancellation notice would reach, and what it would say (MT-163). */
  previewChangeNotice: (groupId: string, eventId: string, body: ChangeNoticeRequest) =>
    api.post<ChangeNoticePreviewResponse>(
      `/api/v1/groups/${groupId}/events/${eventId}/change-notice/preview`,
      body,
    ),

  /** Queues the notice to every household invited to the meal. */
  queueChangeNotice: (groupId: string, eventId: string, body: ChangeNoticeRequest) =>
    api.post<QueuedReminderResponse>(
      `/api/v1/groups/${groupId}/events/${eventId}/change-notice`,
      body,
    ),

  /** The reminders already sent about this meal. */
  eventReminders: (groupId: string, eventId: string) =>
    api.get<ReminderSentResponse[]>(`/api/v1/groups/${groupId}/events/${eventId}/reminders`),

  /**
   * The link to drop into a group text (SPEC §6.28).
   *
   * Derived from the meal rather than issued, so asking twice gives the same
   * link and there is nothing to clean up after a revoke.
   */
  eventShareLink: (groupId: string, eventId: string) =>
    api.get<EventShareLinkResponse>(`/api/v1/groups/${groupId}/events/${eventId}/share`),

  /**
   * Stops every copy of the old link and returns the replacement.
   *
   * One increment of the meal's share version, which is inside the signed
   * token — so every copy already pasted into every thread stops working at
   * the same moment.
   */
  revokeEventShareLink: (groupId: string, eventId: string) =>
    api.post<EventShareLinkResponse>(
      `/api/v1/groups/${groupId}/events/${eventId}/share/revoke`,
      {},
    ),

  /**
   * The household home screen (SPEC §6.11, §8.6).
   *
   * The next meal, this household's answer to it, the menu and the totals, in
   * one call. `next` is null when nothing is scheduled, which is an ordinary
   * state rather than a failure.
   */
  familyHome: () => api.get<FamilyHomeResponse>('/api/v1/family/home'),

  /**
   * One meal, and what this household has said about it (SPEC §8.6).
   *
   * Everything the RSVP screen needs in one round trip. It is opened from a
   * text message, outdoors, and a second request that waits for the first is a
   * second chance to fail.
   */
  familyMeal: (eventId: string) => api.get<FamilyMealResponse>(`/api/v1/family/events/${eventId}`),

  /** How many are coming, as this household is allowed to see it (SPEC §6.31.1). */
  familyEventAttendance: (eventId: string) =>
    api.get<AttendanceSummaryResponse>(`/api/v1/family/events/${eventId}/attendance`),

  /** Offers an email address for this household and sends it a proof (MT-182). */
  addFamilyEmail: (body: AddFamilyEmailRequest) =>
    api.post<AddFamilyEmailResponse>('/api/v1/family/contacts/email', body),

  /** What this household has coming, and what it has brought (SPEC §8.7, §6.30). */
  familyHistory: () => api.get<FamilyHistoryResponse>('/api/v1/family/history'),

  /** Says whether this household is coming (SPEC §6.12). */
  updateFamilyRsvp: (eventId: string, body: SaveFamilyRsvpRequest) =>
    api.put<FamilyRsvpResponse>(`/api/v1/family/events/${eventId}/rsvp`, body),

  /** Says this household will bring something (SPEC §6.20). */
  claimContribution: (body: SaveContributionRequest) =>
    api.post<ContributionClaimResponse>('/api/v1/family/contributions', body),

  /** Changes how much, or what, this household is bringing. */
  amendContribution: (claimId: string, body: SaveContributionRequest) =>
    api.put<ContributionClaimResponse>(`/api/v1/family/contributions/${claimId}`, body),

  /** Gives a claim back. */
  releaseContribution: (claimId: string) =>
    api.delete<void>(`/api/v1/family/contributions/${claimId}`),

  /** Who in this household has opened their link. Masked numbers only. */
  familyContacts: () => api.get<HouseholdContactResponse[]>('/api/v1/family/contacts'),

  /**
   * A group's meal templates (SPEC §6.16).
   *
   * Summaries only. A list of twelve templates does not need four hundred rows
   * of menu to draw twelve names.
   */
  listMealTemplates: (groupId: string, includeArchived = false) =>
    api.get<MealTemplateSummaryResponse[]>(
      `/api/v1/groups/${groupId}/meal-templates?includeArchived=${includeArchived}`,
    ),

  /** One template, with its whole menu. */
  getMealTemplate: (groupId: string, templateId: string) =>
    api.get<MealTemplateResponse>(`/api/v1/groups/${groupId}/meal-templates/${templateId}`),

  createMealTemplate: (groupId: string, body: SaveMealTemplateRequest) =>
    api.post<MealTemplateResponse>(`/api/v1/groups/${groupId}/meal-templates`, body),

  /**
   * Saves a template's name, description and whole menu in one call.
   *
   * The whole tree rather than an edit per row, because that is what an editor
   * has: somebody rearranges four items and renames a section, then presses
   * save once.
   */
  updateMealTemplate: (groupId: string, templateId: string, body: SaveMealTemplateRequest) =>
    api.put<MealTemplateResponse>(`/api/v1/groups/${groupId}/meal-templates/${templateId}`, body),

  duplicateMealTemplate: (
    groupId: string,
    templateId: string,
    body: DuplicateMealTemplateRequest,
  ) =>
    api.post<MealTemplateResponse>(
      `/api/v1/groups/${groupId}/meal-templates/${templateId}/duplicate`,
      body,
    ),

  archiveMealTemplate: (groupId: string, templateId: string) =>
    api.post<void>(`/api/v1/groups/${groupId}/meal-templates/${templateId}/archive`),

  unarchiveMealTemplate: (groupId: string, templateId: string) =>
    api.post<void>(`/api/v1/groups/${groupId}/meal-templates/${templateId}/unarchive`),

  /** The order the meals go in, and what comes next (SPEC §6.18). */
  getMealRotation: (groupId: string) =>
    api.get<MealRotationResponse>(`/api/v1/groups/${groupId}/rotation`),

  createMealEvent: (groupId: string, body: SaveMealEventRequest) =>
    api.post<MealEventResponse>(`/api/v1/groups/${groupId}/events`, body),

  /**
   * One page of a group's meals (SPEC §8.12, §11.6).
   *
   * `view` rather than a status and a date the caller combines. "Upcoming" is
   * published *and still to come*, and that rule belongs on the server rather
   * than in every screen that asks.
   */
  listMealEvents: (groupId: string, view: string, page = 1, pageSize = 50) =>
    api.get<MealEventPageResponse>(
      `/api/v1/groups/${groupId}/events?view=${view}&page=${page}&pageSize=${pageSize}`,
    ),

  getMealEvent: (groupId: string, eventId: string) =>
    api.get<MealEventResponse>(`/api/v1/groups/${groupId}/events/${eventId}`),

  updateMealEvent: (groupId: string, eventId: string, body: SaveMealEventRequest) =>
    api.put<MealEventResponse>(`/api/v1/groups/${groupId}/events/${eventId}`, body),

  publishMealEvent: (groupId: string, eventId: string) =>
    api.post<MealEventResponse>(`/api/v1/groups/${groupId}/events/${eventId}/publish`),

  cancelMealEvent: (groupId: string, eventId: string) =>
    api.post<MealEventResponse>(`/api/v1/groups/${groupId}/events/${eventId}/cancel`),

  /**
   * What to tell the families, after calling a meal off (SPEC §6.24.1).
   *
   * Asked for after the cancellation rather than before, so the message
   * describes something that has actually happened.
   */
  cancellationNotice: (groupId: string, eventId: string) =>
    api.get<CancellationNoticeResponse>(
      `/api/v1/groups/${groupId}/events/${eventId}/cancellation-notice`,
    ),

  archiveMealEvent: (groupId: string, eventId: string) =>
    api.post<MealEventResponse>(`/api/v1/groups/${groupId}/events/${eventId}/archive`),

  /** A meal's own menu, with what families have promised against each item. */
  getEventMenu: (groupId: string, eventId: string) =>
    api.get<EventMenuResponse>(`/api/v1/groups/${groupId}/events/${eventId}/menu`),

  /**
   * Saves a meal's whole menu.
   *
   * `confirm` is how an organizer says to go ahead with an edit that would
   * discard something a family already promised. Without it the server refuses
   * and says who would lose what (SPEC §6.19, MT-090).
   */
  saveEventMenu: (groupId: string, eventId: string, body: SaveEventMenuRequest) =>
    api.put<EventMenuResponse>(`/api/v1/groups/${groupId}/events/${eventId}/menu`, body),

  /** Turns a meal that worked into a template (SPEC §6.29). */
  saveEventAsTemplate: (
    groupId: string,
    eventId: string,
    body: { name: string; description?: string | null },
  ) =>
    api.post<MealTemplateResponse>(
      `/api/v1/groups/${groupId}/events/${eventId}/save-as-template`,
      body,
    ),

  /** Copies a template's menu onto a meal (SPEC §6.17). */
  assignMealTemplateToEvent: (groupId: string, eventId: string, body: AssignMealTemplateRequest) =>
    api.post<unknown>(`/api/v1/groups/${groupId}/events/${eventId}/menu/from-template`, body),

  /**
   * Opens a household's view of the product for this organizer (SPEC §E-02).
   *
   * A second cookie beside the organizer's, never instead of it. The household
   * session it creates has no organizer powers, and the organizer's own session
   * keeps working — going back is a link, not a sign-in.
   */
  viewAsHousehold: (householdId: string) =>
    api.post<ViewAsHouseholdResponse>('/api/v1/organizers/me/household', { householdId }),
}

export { api, ApiError } from './client-exports'
